import { getStorage, ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import imageCompression from 'browser-image-compression';

class StorageService {
  // Firebase Storage에서 public download URL 생성
  async getPublicFileUrl(filePath: string): Promise<string> {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);
    const url = await getDownloadURL(fileRef);
    return url;
  }

  // 파일 업로드
  async uploadFile(filePath: string, file: File): Promise<string> {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);
    await uploadBytes(fileRef, file);
    return await this.getPublicFileUrl(filePath);
  }

  // 이미지 업로드 (금형 이미지용)
  async uploadImage(file: File, moldId: string, index: number): Promise<string> {
    const fileName = `${Date.now()}_${index}_${file.name}`;
    const filePath = `molds/${moldId}/images/${fileName}`;
    return await this.uploadFile(filePath, file);
  }

  // 이미지 + 썸네일 업로드 (원본 + 500x500 썸네일)
  async uploadImageWithThumbnail(
    file: File, 
    moldId: string, 
    index: number
  ): Promise<{ original: string; thumbnail: string }> {
    const timestamp = Date.now();
    const baseName = file.name.replace(/\.[^/.]+$/, ''); // 확장자 제거
    const ext = file.name.split('.').pop() || 'jpg';

    // 원본 업로드
    const originalFileName = `${timestamp}_${index}_${file.name}`;
    const originalPath = `molds/${moldId}/images/${originalFileName}`;
    const originalUrl = await this.uploadFile(originalPath, file);

    // 썸네일 생성 (500x500)
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 500,
      useWebWorker: true,
    });

    // 썸네일 업로드
    const thumbnailFileName = `${timestamp}_${index}_thumb_${baseName}.${ext}`;
    const thumbnailPath = `molds/${moldId}/thumbnails/${thumbnailFileName}`;
    const thumbnailUrl = await this.uploadFile(thumbnailPath, thumbnailFile);

    return { original: originalUrl, thumbnail: thumbnailUrl };
  }

  // 3D 파일 업로드
  async upload3DFile(file: File, moldId: string): Promise<{ url: string; name: string; size: number }> {
    const fileName = `${Date.now()}_${file.name}`;
    const filePath = `molds/${moldId}/3d/${fileName}`;
    const url = await this.uploadFile(filePath, file);
    return {
      url,
      name: file.name,
      size: file.size
    };
  }

  // Customer 로고 업로드 (원본 + 200x200 썸네일)
  async uploadCustomerLogo(
    file: File, 
    customerId: string
  ): Promise<{ logo: string; logoThumbnail: string }> {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';

    // 원본 업로드
    const originalFileName = `${timestamp}_logo.${ext}`;
    const originalPath = `customers/${customerId}/${originalFileName}`;
    const logo = await this.uploadFile(originalPath, file);

    // 썸네일 생성 (200x200)
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 200,
      useWebWorker: true,
    });

    const thumbnailFileName = `${timestamp}_logo_thumb.${ext}`;
    const thumbnailPath = `customers/${customerId}/${thumbnailFileName}`;
    const logoThumbnail = await this.uploadFile(thumbnailPath, thumbnailFile);

    return { logo, logoThumbnail };
  }

  // Location 이미지 업로드 (원본 + 500x500 썸네일)
  async uploadLocationImage(
    file: File, 
    locationId: string, 
    index: number
  ): Promise<{ original: string; thumbnail: string }> {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';

    // 원본 업로드
    const originalFileName = `${timestamp}_${index}.${ext}`;
    const originalPath = `locations/${locationId}/images/${originalFileName}`;
    const original = await this.uploadFile(originalPath, file);

    // 썸네일 생성 (500x500)
    const thumbnailFile = await imageCompression(file, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 500,
      useWebWorker: true,
    });

    const thumbnailFileName = `${timestamp}_${index}_thumb.${ext}`;
    const thumbnailPath = `locations/${locationId}/thumbnails/${thumbnailFileName}`;
    const thumbnail = await this.uploadFile(thumbnailPath, thumbnailFile);

    return { original, thumbnail };
  }

  // 파일 삭제
  async deleteFile(filePath: string): Promise<void> {
    const storage = getStorage();
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  }

  // 여러 파일 삭제
  async deleteFiles(fileUrls: string[]): Promise<void> {
    const storage = getStorage();
    for (const url of fileUrls) {
      try {
        // URL에서 경로 추출
        const urlObj = new URL(url);
        const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
        if (pathMatch) {
          const filePath = decodeURIComponent(pathMatch[1]);
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
        }
      } catch (error) {
        console.error('파일 삭제 실패:', url, error);
      }
    }
  }
}

const storageService = new StorageService();
export default storageService;
